import { useState, useEffect, useCallback } from "react";
import { X, Plus, Zap, Users, LayoutGrid, RefreshCw, Copy, Check, ChevronDown, ChevronRight, Music, MapPin, Globe } from "lucide-react";
import { T } from "../theme";

const API = process.env.REACT_APP_API_URL || "";
// Public site origin for shareable links (Railway production domain)
const PUBLIC_ORIGIN = "https://kandikonnect.app";

// Admin key — sent as x-admin-key on /api/admin/* and event sync calls.
// The backend requires it whenever ADMIN_KEY is set in its environment.
const ADMIN_KEY_STORAGE = "kk_admin_key";
export function adminHeaders(extra = {}) {
  const key = localStorage.getItem(ADMIN_KEY_STORAGE);
  return key ? { ...extra, "x-admin-key": key } : extra;
}


const QUICK_COLORS = [
  "#ff3cac","#b44fff","#00f5ff","#ffd700",
  "#ff6b6b","#4ecdc4","#54a0ff","#39ff14",
];

const SHAPES = ["pony","star","heart","cube","diamond","hex","flower"];

// ─── Tiny shared UI ───────────────────────────────────────────────────────────

function Label({ children }) {
  return (
    <p style={{
      margin: "0 0 6px", fontSize: "0.7rem", fontWeight: 700,
      textTransform: "uppercase", letterSpacing: "0.1em", color: T.muted,
    }}>
      {children}
    </p>
  );
}

function Input({ value, onChange, placeholder, style }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: "100%", padding: "9px 12px",
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 10, color: T.text, fontSize: "0.88rem",
        outline: "none", boxSizing: "border-box", ...style,
      }}
    />
  );
}

function Btn({ onClick, disabled, children, color = T.purple, style }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "9px 18px", borderRadius: 10,
        background: `linear-gradient(135deg, ${color}44, ${color}22)`,
        border: `1px solid ${color}55`,
        color: T.text, fontWeight: 700, fontSize: "0.85rem",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1, transition: "all 0.15s", ...style,
      }}
    >
      {children}
    </button>
  );
}

function CopyBtn({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <button
      onClick={copy}
      title="Copy"
      style={{
        padding: "3px 8px", borderRadius: 6,
        background: copied ? "rgba(57,255,20,0.15)" : "rgba(255,255,255,0.07)",
        border: `1px solid ${copied ? T.green : "rgba(255,255,255,0.12)"}`,
        color: copied ? T.green : T.muted,
        cursor: "pointer", fontSize: "0.72rem", fontWeight: 600,
        display: "inline-flex", alignItems: "center", gap: 4,
      }}
    >
      {copied ? <Check size={11} /> : <Copy size={11} />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function EventAutocomplete({ value, onChange, onSelect }) {
  const [query, setQuery]       = useState(value || "");
  const [results, setResults]   = useState([]);
  const [open, setOpen]         = useState(false);
  const [selected, setSelected] = useState(null);
  const debounceRef = useState({ timer: null })[0];

  const search = (q) => {
    setQuery(q);
    onChange(q);
    setSelected(null);
    if (onSelect) onSelect(null);

    clearTimeout(debounceRef.timer);
    if (q.length < 2) { setResults([]); setOpen(false); return; }

    debounceRef.timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/events/search?q=${encodeURIComponent(q)}`);
        const data = await res.json();
        setResults(data.events || []);
        setOpen(true);
      } catch {
        setResults([]);
      }
    }, 250);
  };

  const pick = (ev) => {
    const display = `${ev.name} — ${ev.venue?.city || ""}, ${ev.venue?.state || ""}`;
    setQuery(display);
    setSelected(ev);
    setOpen(false);
    onChange(display);
    if (onSelect) onSelect(ev);
  };

  return (
    <div style={{ position: "relative" }}>
      <input
        value={query}
        onChange={(e) => search(e.target.value)}
        onFocus={() => { if (results.length > 0 && !selected) setOpen(true); }}
        placeholder="Search events or type a location..."
        style={{
          width: "100%", padding: "9px 12px",
          background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 10, color: T.text, fontSize: "0.88rem",
          outline: "none", boxSizing: "border-box",
        }}
      />
      {selected && (
        <div style={{
          display: "flex", alignItems: "center", gap: 6, marginTop: 4,
        }}>
          <MapPin size={10} color={T.cyan} />
          <span style={{ fontSize: "0.68rem", color: T.cyan }}>
            {selected.venue?.lat?.toFixed(3)}, {selected.venue?.lng?.toFixed(3)}
          </span>
          {selected.link && (
            <a href={selected.link} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: "0.68rem", color: T.muted, textDecoration: "none", marginLeft: "auto" }}>
              Edmtrain →
            </a>
          )}
        </div>
      )}
      {open && results.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50,
          marginTop: 4, borderRadius: 10, overflow: "hidden",
          background: "rgba(16,8,32,0.98)", border: "1px solid rgba(255,255,255,0.12)",
          maxHeight: 200, overflowY: "auto",
        }}>
          {results.slice(0, 15).map(ev => (
            <button
              key={ev.id}
              onClick={() => pick(ev)}
              style={{
                display: "flex", alignItems: "center", gap: 8, width: "100%",
                padding: "9px 12px", border: "none",
                borderBottom: "1px solid rgba(255,255,255,0.05)",
                background: "transparent", cursor: "pointer", textAlign: "left",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "rgba(180,79,255,0.12)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <Music size={12} color={T.purple} style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: "0.8rem", fontWeight: 600, color: T.text,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {ev.name}
                </div>
                <div style={{ fontSize: "0.68rem", color: T.muted }}>
                  {ev.venue?.name ? `${ev.venue.name} — ` : ""}{ev.venue?.city}{ev.venue?.state ? `, ${ev.venue.state}` : ""}
                  {ev.date ? ` · ${new Date(ev.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}
                </div>
              </div>
            </button>
          ))}
          <div style={{ padding: "5px 12px", textAlign: "center" }}>
            <a href="https://edmtrain.com" target="_blank" rel="noopener noreferrer"
              style={{ fontSize: "0.6rem", color: T.muted, textDecoration: "none" }}>
              Event data powered by Edmtrain
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function ResultBox({ data, error }) {
  if (!data && !error) return null;
  return (
    <div style={{
      marginTop: 10,
      background: error ? "rgba(255,68,68,0.08)" : "rgba(57,255,20,0.06)",
      border: `1px solid ${error ? "rgba(255,68,68,0.3)" : "rgba(57,255,20,0.2)"}`,
      borderRadius: 10, padding: "10px 12px",
    }}>
      <pre style={{
        margin: 0, color: error ? T.red : T.green,
        fontSize: "0.75rem", whiteSpace: "pre-wrap", wordBreak: "break-all",
        fontFamily: "monospace",
      }}>
        {error || JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}


// ─── Tab: Create Kandi ────────────────────────────────────────────────────────

function CreateTab() {
  const user   = JSON.parse(localStorage.getItem("user") || "null");
  const [name,     setName]     = useState("Test Kandi");
  const [emoji,    setEmoji]    = useState("✨");
  const [colors,   setColors]   = useState(["#ff3cac", "#b44fff", "#00f5ff"]);
  const [shapes,   setShapes]   = useState(["pony", "star", "heart"]);
  const [userId,   setUserId]   = useState(user?.id || "");
  const [loading,  setLoading]  = useState(false);
  const [result,   setResult]   = useState(null);
  const [error,    setError]    = useState(null);

  const toggleColor = (c) => {
    setColors((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]
    );
  };

  const submit = async () => {
    setLoading(true); setResult(null); setError(null);
    try {
      const res  = await fetch(`${API}/api/kandi/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, name, emoji, colors, shapes, username: user?.username }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <Label>User ID (creator)</Label>
        <Input value={userId} onChange={setUserId} placeholder="MongoDB ObjectId" />
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: 1 }}>
          <Label>Name</Label>
          <Input value={name} onChange={setName} placeholder="Kandi name" />
        </div>
        <div style={{ width: 80 }}>
          <Label>Emoji</Label>
          <Input value={emoji} onChange={setEmoji} placeholder="✨" />
        </div>
      </div>

      <div>
        <Label>Colors (toggle)</Label>
        <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
          {QUICK_COLORS.map((c) => (
            <div
              key={c}
              onClick={() => toggleColor(c)}
              style={{
                width: 28, height: 28, borderRadius: "50%", cursor: "pointer",
                background: `radial-gradient(circle at 35% 35%, rgba(255,255,255,0.5) 0%, ${c} 55%)`,
                border: colors.includes(c) ? "2.5px solid #fff" : "2px solid rgba(255,255,255,0.15)",
                boxShadow: colors.includes(c) ? `0 0 8px ${c}` : "none",
                transform: colors.includes(c) ? "scale(1.15)" : "scale(1)",
                transition: "all 0.12s",
              }}
            />
          ))}
        </div>
        <p style={{ margin: "5px 0 0", color: T.muted, fontSize: "0.72rem" }}>
          Selected: {colors.join(", ") || "none"}
        </p>
      </div>

      <div>
        <Label>Shapes (click to cycle)</Label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {shapes.map((s, i) => (
            <button
              key={i}
              onClick={() => {
                const next = SHAPES[(SHAPES.indexOf(s) + 1) % SHAPES.length];
                setShapes((prev) => prev.map((x, j) => j === i ? next : x));
              }}
              style={{
                padding: "4px 10px", borderRadius: 8,
                background: "rgba(180,79,255,0.15)",
                border: "1px solid rgba(180,79,255,0.35)",
                color: T.purple, fontSize: "0.78rem", fontWeight: 700, cursor: "pointer",
              }}
            >
              {s}
            </button>
          ))}
          <button
            onClick={() => setShapes((p) => [...p, "pony"])}
            style={{
              padding: "4px 10px", borderRadius: 8,
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: T.muted, fontSize: "0.78rem", cursor: "pointer",
            }}
          >
            + add
          </button>
        </div>
      </div>

      <Btn onClick={submit} disabled={loading || !userId} color={T.purple} style={{ width: "100%" }}>
        {loading ? "Creating…" : "Create Kandi"}
      </Btn>

      {result?.kandi && (
        <div style={{
          background: "rgba(57,255,20,0.06)", border: "1px solid rgba(57,255,20,0.2)",
          borderRadius: 12, padding: "14px 14px",
        }}>
          <p style={{ margin: "0 0 8px", color: T.green, fontWeight: 700, fontSize: "0.85rem" }}>
            ✅ Created — save these for scanning
          </p>
          {[["Public ID", result.kandi.id], ["Secret UID", result.kandi.uid]].map(([label, val]) => (
            <div key={label} style={{ marginBottom: 8 }}>
              <Label>{label}</Label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <code style={{
                  flex: 1, padding: "6px 10px", borderRadius: 8,
                  background: "rgba(255,255,255,0.06)", color: T.text,
                  fontSize: "0.78rem", fontFamily: "monospace", wordBreak: "break-all",
                }}>
                  {val}
                </code>
                <CopyBtn value={val} />
              </div>
            </div>
          ))}
          {[
            ["Kandi Link (public, read-only)", `${PUBLIC_ORIGIN}/kandi/${result.kandi.id}`],
            ["Scan Link (with UID — for QR/NFC)", `${PUBLIC_ORIGIN}/kandi/${result.kandi.id}?uid=${result.kandi.uid}`],
          ].map(([label, url]) => (
            <div key={label} style={{ marginBottom: 8 }}>
              <Label>{label}</Label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    flex: 1, padding: "6px 10px", borderRadius: 8,
                    background: "rgba(255,255,255,0.06)", color: T.cyan,
                    fontSize: "0.78rem", fontFamily: "monospace", wordBreak: "break-all",
                    textDecoration: "none",
                  }}
                >
                  {url}
                </a>
                <CopyBtn value={url} />
              </div>
            </div>
          ))}
        </div>
      )}

      {error && <ResultBox error={error} />}
    </div>
  );
}

// ─── Tab: Scan / Claim ────────────────────────────────────────────────────────

function ScanClaimTab() {
  const user         = JSON.parse(localStorage.getItem("user") || "null");
  const [kandiId,    setKandiId]    = useState("");
  const [uid,        setUid]        = useState("");
  const [userId,     setUserId]     = useState(user?.id || "");
  const [username,   setUsername]   = useState(user?.username || "");
  const [location,   setLocation]   = useState("");
  const [lat,        setLat]        = useState(null);
  const [lng,        setLng]        = useState(null);
  const [message,    setMessage]    = useState("Test scan from admin panel");
  const [mode,       setMode]       = useState("scan"); // "scan" | "claim" | "verify"
  const [loading,    setLoading]    = useState(false);
  const [result,     setResult]     = useState(null);
  const [error,      setError]      = useState(null);

  const submit = async () => {
    setLoading(true); setResult(null); setError(null);
    try {
      let endpoint, body;
      if (mode === "verify") {
        endpoint = `${API}/api/kandi/${kandiId}/verify`;
        body     = { uid };
      } else if (mode === "scan") {
        endpoint = `${API}/api/kandi/${kandiId}/scan`;
        body     = { uid, userId, username, location, lat, lng, message };
      } else {
        endpoint = `${API}/api/kandi/${kandiId}/claim`;
        body     = { uid, userId, username, location, lat, lng };
      }
      const res  = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const modeColor = { scan: T.cyan, claim: T.gold, verify: T.purple }[mode];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Mode selector */}
      <div style={{ display: "flex", gap: 6 }}>
        {["verify", "scan", "claim"].map((m) => (
          <button
            key={m}
            onClick={() => { setMode(m); setResult(null); setError(null); }}
            style={{
              flex: 1, padding: "8px 0", borderRadius: 10,
              fontWeight: 700, fontSize: "0.82rem", cursor: "pointer",
              textTransform: "capitalize",
              background: mode === m
                ? `linear-gradient(135deg, ${modeColor}33, ${modeColor}18)`
                : "rgba(255,255,255,0.04)",
              border: mode === m
                ? `1px solid ${modeColor}55`
                : "1px solid rgba(255,255,255,0.08)",
              color: mode === m ? T.text : T.muted,
              transition: "all 0.15s",
            }}
          >
            {m}
          </button>
        ))}
      </div>

      <div>
        <Label>Kandi ID (KK-XXXXXX)</Label>
        <Input value={kandiId} onChange={setKandiId} placeholder="KK-ABC123" />
      </div>
      <div>
        <Label>Secret UID</Label>
        <Input value={uid} onChange={setUid} placeholder="uid-…" />
      </div>

      {mode !== "verify" && (
        <>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <Label>User ID</Label>
              <Input value={userId} onChange={setUserId} placeholder="MongoDB ObjectId" />
            </div>
            <div style={{ flex: 1 }}>
              <Label>Username</Label>
              <Input value={username} onChange={setUsername} placeholder="username" />
            </div>
          </div>
          <div>
            <Label>Location</Label>
            <EventAutocomplete
              value={location}
              onChange={setLocation}
              onSelect={(ev) => {
                if (ev) {
                  setLat(ev.venue?.lat || null);
                  setLng(ev.venue?.lng || null);
                } else {
                  setLat(null);
                  setLng(null);
                }
              }}
            />
          </div>
          {mode === "scan" && (
            <div>
              <Label>Message</Label>
              <Input value={message} onChange={setMessage} placeholder="Scan message" />
            </div>
          )}
        </>
      )}

      <Btn onClick={submit} disabled={loading || !kandiId || !uid} color={modeColor} style={{ width: "100%" }}>
        {loading ? "Working…" : `Run ${mode}`}
      </Btn>

      <ResultBox data={result} error={error} />
    </div>
  );
}

// ─── Tab: Browse All Kandi ────────────────────────────────────────────────────

function KandiRow({ k, onDelete, onReset }) {
  const [open,     setOpen]     = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resetting,setResetting]= useState(false);

  return (
    <div style={{
      background: T.card, border: T.border, borderRadius: 12,
      overflow: "hidden", marginBottom: 8,
    }}>
      {/* Header row */}
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 12px", cursor: "pointer",
        }}
      >
        {open ? <ChevronDown size={13} color={T.muted} /> : <ChevronRight size={13} color={T.muted} />}
        <span style={{ fontSize: "1rem" }}>{k.emoji || "✨"}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, color: T.text, fontWeight: 600, fontSize: "0.85rem" }}>{k.name}</p>
          <p style={{ margin: 0, color: T.muted, fontSize: "0.72rem", fontFamily: "monospace" }}>{k.id}</p>
        </div>
        <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
          <span style={{
            padding: "2px 8px", borderRadius: 99, fontSize: "0.68rem", fontWeight: 700,
            background: "rgba(0,245,255,0.1)", border: "1px solid rgba(0,245,255,0.2)", color: T.cyan,
          }}>
            {k.scanCount} scans
          </span>
        </div>
      </div>

      {/* Expanded detail */}
      {open && (
        <div style={{ padding: "0 12px 12px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>

            {[["Public ID", k.id], ["Secret UID", k.uid]].map(([label, val]) => val && (
              <div key={label}>
                <Label>{label}</Label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <code style={{
                    flex: 1, padding: "5px 9px", borderRadius: 7,
                    background: "rgba(255,255,255,0.06)", color: T.text,
                    fontSize: "0.72rem", fontFamily: "monospace", wordBreak: "break-all",
                  }}>
                    {val}
                  </code>
                  <CopyBtn value={val} />
                </div>
              </div>
            ))}

            <div>
              <Label>Owner</Label>
              <p style={{ margin: 0, color: T.text, fontSize: "0.8rem" }}>
                {k.currentOwner?.username || k.currentOwner || "—"}
              </p>
            </div>

            {k.history?.length > 0 && (
              <div>
                <Label>History ({k.history.length})</Label>
                {k.history.slice(0, 4).map((h, i) => (
                  <p key={i} style={{ margin: "2px 0", color: T.muted, fontSize: "0.72rem" }}>
                    [{h.actionType}] {h.username} @ {h.location} — {h.date ? new Date(h.date).toLocaleDateString() : "?"}
                  </p>
                ))}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <Btn
                onClick={async () => {
                  setResetting(true);
                  await onReset(k.id);
                  setResetting(false);
                }}
                disabled={resetting}
                color={T.gold}
                style={{ flex: 1, padding: "7px" }}
              >
                {resetting ? "…" : "Reset"}
              </Btn>
              <Btn
                onClick={async () => {
                  if (!window.confirm(`Delete ${k.id}?`)) return;
                  setDeleting(true);
                  await onDelete(k.id);
                  setDeleting(false);
                }}
                disabled={deleting}
                color={T.red}
                style={{ flex: 1, padding: "7px" }}
              >
                {deleting ? "…" : "Delete"}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BrowseTab() {
  const [kandis,   setKandis]   = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [search,   setSearch]   = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res  = await fetch(`${API}/api/admin/kandi`, { headers: adminHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      setKandis(data.kandis);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    await fetch(`${API}/api/admin/kandi/${id}`, { method: "DELETE", headers: adminHeaders() });
    setKandis((prev) => prev.filter((k) => k.id !== id));
  };

  const handleReset = async (id) => {
    const res  = await fetch(`${API}/api/admin/reset-kandi/${id}`, { method: "POST", headers: adminHeaders() });
    const data = await res.json();
    if (data.success) {
      setKandis((prev) => prev.map((k) => k.id === id ? { ...k, ...data.kandi } : k));
    }
  };

  const filtered = kandis.filter((k) =>
    !search || k.id.toLowerCase().includes(search.toLowerCase()) || k.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <Input value={search} onChange={setSearch} placeholder="Search by name or ID…" style={{ flex: 1 }} />
        <button
          onClick={load}
          disabled={loading}
          style={{
            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
            background: "rgba(255,255,255,0.05)", border: T.border,
            color: T.muted, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <RefreshCw size={14} style={{ animation: loading ? "spin 0.8s linear infinite" : "none" }} />
        </button>
      </div>

      {error && <p style={{ color: T.red, fontSize: "0.82rem", marginBottom: 10 }}>{error}</p>}

      {loading && !kandis.length ? (
        <p style={{ color: T.muted, textAlign: "center", padding: "28px 0", fontSize: "0.85rem" }}>Loading…</p>
      ) : filtered.length === 0 ? (
        <p style={{ color: T.muted, textAlign: "center", padding: "28px 0", fontSize: "0.85rem" }}>
          {kandis.length === 0 ? "No kandi yet — create one first" : "No matches"}
        </p>
      ) : (
        <>
          <p style={{ color: T.muted, fontSize: "0.72rem", marginBottom: 8 }}>
            {filtered.length} kandi
          </p>
          {filtered.map((k) => (
            <KandiRow key={k.id} k={k} onDelete={handleDelete} onReset={handleReset} />
          ))}
        </>
      )}
    </div>
  );
}

// ─── Tab: Users ───────────────────────────────────────────────────────────────

function UsersTab() {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res  = await fetch(`${API}/api/admin/users`, { headers: adminHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || res.statusText);
      setUsers(data.users);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <p style={{ margin: 0, color: T.muted, fontSize: "0.72rem" }}>{users.length} registered users</p>
        <button
          onClick={load}
          style={{
            background: "none", border: "none", cursor: "pointer", color: T.muted,
            display: "flex", alignItems: "center", gap: 4, fontSize: "0.78rem",
          }}
        >
          <RefreshCw size={12} /> refresh
        </button>
      </div>

      {error && <p style={{ color: T.red, fontSize: "0.82rem" }}>{error}</p>}

      {loading && !users.length ? (
        <p style={{ color: T.muted, textAlign: "center", padding: "28px 0", fontSize: "0.85rem" }}>Loading…</p>
      ) : (
        users.map((u) => (
          <div key={u._id} style={{
            background: T.card, border: T.border, borderRadius: 12,
            padding: "12px 14px", marginBottom: 8,
            display: "flex", alignItems: "center", gap: 12,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
              background: "linear-gradient(135deg, #b44fff, #ff3cac)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "0.75rem", fontWeight: 800, color: "#fff",
            }}>
              {u.username.slice(0, 2).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, color: T.text, fontWeight: 700, fontSize: "0.88rem" }}>{u.username}</p>
              <p style={{ margin: 0, color: T.muted, fontSize: "0.75rem" }}>{u.email}</p>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <CopyBtn value={String(u._id)} />
              <p style={{ margin: "4px 0 0", color: T.muted, fontSize: "0.65rem", fontFamily: "monospace" }}>
                {String(u._id).slice(-6)}
              </p>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ─── Tab: Quick Flow ──────────────────────────────────────────────────────────
// One-click: create kandi → immediately scan it as the logged-in user

function QuickFlowTab() {
  const user      = JSON.parse(localStorage.getItem("user") || "null");
  const [running, setRunning] = useState(false);
  const [steps,   setSteps]   = useState([]);
  const [location, setLocation] = useState("Electric Forest");

  const addStep = (label, ok, data) =>
    setSteps((p) => [...p, { label, ok, data }]);

  const run = async () => {
    if (!user) { setSteps([{ label: "Not logged in — log in first", ok: false }]); return; }
    setRunning(true);
    setSteps([]);

    try {
      // Step 1: Create
      addStep("Creating kandi…", true, null);
      const cr = await fetch(`${API}/api/kandi/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: user.id, username: user.username,
          name: "Quick-Flow Kandi", emoji: "🎪",
          colors: ["#ff3cac", "#b44fff", "#00f5ff"],
          shapes: ["pony", "star", "heart"],
          location,
        }),
      }).then((r) => r.json());
      if (!cr.success) throw new Error(cr.error);
      const { id, uid } = cr.kandi;
      setSteps((p) => p.map((s, i) => i === p.length - 1 ? { ...s, label: `Created: ${id}`, data: cr.kandi } : s));

      // Step 2: Verify UID
      const vr = await fetch(`${API}/api/kandi/${id}/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid }),
      }).then((r) => r.json());
      addStep(vr.verified ? `UID verified ✓` : "UID verify FAILED", vr.verified, vr);
      if (!vr.verified) throw new Error("UID verify failed");

      // Step 3: Scan
      const sr = await fetch(`${API}/api/kandi/${id}/scan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid, userId: user.id, username: user.username, location, message: "Quick-flow test scan" }),
      }).then((r) => r.json());
      addStep(sr.success ? `Scan logged (total: ${sr.kandi.scanCount})` : "Scan FAILED", sr.success, sr);

      addStep(`✅ Done — ID: ${id}`, true, { id, uid });
    } catch (e) {
      addStep(`❌ ${e.message}`, false, null);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{
        background: "rgba(180,79,255,0.06)", border: "1px solid rgba(180,79,255,0.2)",
        borderRadius: 12, padding: "14px",
      }}>
        <p style={{ margin: "0 0 4px", color: T.purple, fontWeight: 700, fontSize: "0.85rem" }}>
          One-click test flow
        </p>
        <p style={{ margin: 0, color: T.muted, fontSize: "0.8rem" }}>
          Creates a new kandi, verifies its UID, then logs a scan — all in one shot. Great for testing the full chain.
        </p>
      </div>

      <div>
        <Label>Location label</Label>
        <Input value={location} onChange={setLocation} placeholder="Festival / city" />
      </div>

      <Btn onClick={run} disabled={running || !user} color={T.purple} style={{ width: "100%" }}>
        {running ? "Running…" : "Run Full Flow"}
      </Btn>

      {!user && (
        <p style={{ color: T.red, fontSize: "0.8rem", margin: 0 }}>Log in first to use quick flow</p>
      )}

      {steps.length > 0 && (
        <div style={{
          background: "rgba(255,255,255,0.03)", border: T.border,
          borderRadius: 12, padding: "12px 14px",
        }}>
          {steps.map((s, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: "0.8rem", color: s.ok ? T.green : T.red, flexShrink: 0 }}>
                {s.ok ? "✓" : "✗"}
              </span>
              <div>
                <p style={{ margin: 0, color: s.ok ? T.text : T.red, fontSize: "0.82rem" }}>{s.label}</p>
                {s.data && s.data.uid && (
                  <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                    <CopyBtn value={s.data.id} />
                    <span style={{ color: T.muted, fontSize: "0.68rem", alignSelf: "center" }}>ID</span>
                    <CopyBtn value={s.data.uid} />
                    <span style={{ color: T.muted, fontSize: "0.68rem", alignSelf: "center" }}>UID</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Events Tab ──────────────────────────────────────────────────────────────

function EventsTab() {
  const [events, setEvents]     = useState([]);
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(false);
  const [syncing, setSyncing]   = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [searchQ, setSearchQ]   = useState("");
  const [searchResults, setSearchResults] = useState(null);

  const fetchCached = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/events/cached`, { headers: adminHeaders() });
      const data = await res.json();
      if (data.success) {
        setEvents(data.events || []);
        setStats(data.stats || null);
      }
    } catch (err) {
      console.error("Fetch cached events error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCached(); }, [fetchCached]);

  const handleSyncAll = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await fetch(`${API}/api/events/sync`, {
        method: "POST",
        headers: adminHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ syncAll: true }),
      });
      const data = await res.json();
      setSyncResult(data);
      await fetchCached();
    } catch (err) {
      setSyncResult({ error: err.message });
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncLocation = async (locationId, label) => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await fetch(`${API}/api/events/sync`, {
        method: "POST",
        headers: adminHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ locationId }),
      });
      const data = await res.json();
      setSyncResult({ ...data, label });
      await fetchCached();
    } catch (err) {
      setSyncResult({ error: err.message });
    } finally {
      setSyncing(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQ.trim() || searchQ.length < 2) return;
    try {
      const res = await fetch(`${API}/api/events/search?q=${encodeURIComponent(searchQ)}`);
      const data = await res.json();
      setSearchResults(data.events || []);
    } catch (err) {
      console.error("Search error:", err);
    }
  };

  const QUICK_CITIES = [
    { label: "LA",       locationId: 73  },
    { label: "Vegas",    locationId: 69  },
    { label: "Miami",    locationId: 87  },
    { label: "NYC",      locationId: 70  },
    { label: "Chicago",  locationId: 71  },
    { label: "Denver",   locationId: 76  },
    { label: "SF",       locationId: 72  },
    { label: "Atlanta",  locationId: 84  },
    { label: "Detroit",  locationId: 102 },
    { label: "Orlando",  locationId: 94  },
    { label: "Phoenix",  locationId: 98  },
    { label: "Tucson",   locationId: 489 },
    { label: "Toronto",  locationId: 74  },
    { label: "Montreal", locationId: 89  },
    { label: "Vancouver",locationId: 82  },
    { label: "Calgary",  locationId: 106 },
  ];

  const displayEvents = searchResults !== null ? searchResults : events;

  return (
    <div>
      {/* Stats */}
      {stats && (
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14,
        }}>
          {[
            { v: stats.total, l: "Events", c: T.cyan },
            { v: stats.uniqueVenues, l: "Venues", c: T.purple },
            { v: stats.uniqueCities, l: "Cities", c: T.pink },
          ].map(s => (
            <div key={s.l} style={{
              textAlign: "center", padding: "10px 8px",
              background: T.card, border: T.border, borderRadius: 12,
            }}>
              <div style={{ fontSize: "1.3rem", fontWeight: 800, color: s.c }}>{s.v}</div>
              <div style={{ fontSize: "0.65rem", color: T.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>{s.l}</div>
            </div>
          ))}
        </div>
      )}

      {/* Sync controls */}
      <Label>Sync from Edmtrain</Label>
      <Btn
        onClick={handleSyncAll}
        disabled={syncing}
        color={T.cyan}
        style={{ width: "100%", marginBottom: 8 }}
      >
        <Globe size={14} />
        {syncing ? "Syncing all regions..." : "Sync All Regions (18 regions)"}
      </Btn>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
        {QUICK_CITIES.map(c => (
          <button
            key={c.label}
            onClick={() => handleSyncLocation(c.locationId, c.label)}
            disabled={syncing}
            style={{
              padding: "5px 10px", borderRadius: 8, fontSize: "0.72rem", fontWeight: 600,
              background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
              color: T.text, cursor: syncing ? "not-allowed" : "pointer",
              opacity: syncing ? 0.5 : 1,
            }}
          >
            <MapPin size={10} style={{ verticalAlign: -1, marginRight: 3 }} />
            {c.label}
          </button>
        ))}
      </div>

      {syncResult && (
        <div style={{
          padding: "8px 12px", borderRadius: 10, marginBottom: 14, fontSize: "0.8rem",
          background: syncResult.error ? "rgba(255,68,68,0.1)" : "rgba(57,255,20,0.08)",
          border: syncResult.error ? "1px solid rgba(255,68,68,0.25)" : "1px solid rgba(57,255,20,0.2)",
          color: syncResult.error ? T.red : T.green,
        }}>
          {syncResult.error
            ? `Error: ${syncResult.error}`
            : `Synced ${syncResult.synced} events${syncResult.label ? ` for ${syncResult.label}` : syncResult.regions ? ` across ${syncResult.regions} regions` : ""}`}
        </div>
      )}

      {/* Search */}
      <Label>Search Events</Label>
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        <Input
          value={searchQ}
          onChange={setSearchQ}
          placeholder="Search by name, venue, city..."
          style={{ flex: 1 }}
        />
        <Btn onClick={handleSearch} color={T.purple} style={{ padding: "9px 14px", flexShrink: 0 }}>
          Go
        </Btn>
      </div>
      {searchResults !== null && (
        <button
          onClick={() => { setSearchResults(null); setSearchQ(""); }}
          style={{
            background: "none", border: "none", color: T.cyan,
            fontSize: "0.75rem", cursor: "pointer", marginBottom: 10, padding: 0,
          }}
        >
          ← Back to all cached events
        </button>
      )}

      {/* Event list */}
      <Label>{searchResults !== null ? `${displayEvents.length} search results` : `${events.length} cached events`}</Label>

      {loading ? (
        <p style={{ color: T.muted, textAlign: "center", padding: 20 }}>Loading...</p>
      ) : displayEvents.length === 0 ? (
        <p style={{ color: T.muted, textAlign: "center", padding: 20, fontSize: "0.85rem" }}>
          No events cached. Hit "Sync All Regions" to pull from Edmtrain.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {displayEvents.slice(0, 50).map(ev => (
            <div key={ev.id || ev.edmTrainId} style={{
              padding: "10px 12px", borderRadius: 12,
              background: T.card, border: T.border,
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                  background: "linear-gradient(135deg, rgba(180,79,255,0.2), rgba(255,60,172,0.2))",
                  border: "1px solid rgba(180,79,255,0.25)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Music size={13} color={T.purple} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    margin: 0, fontWeight: 700, fontSize: "0.82rem", color: T.text,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {ev.name}
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: "0.7rem", color: T.muted }}>
                    <MapPin size={9} style={{ verticalAlign: -1 }} /> {ev.venue?.name}{ev.venue?.city ? `, ${ev.venue.city}` : ""}
                  </p>
                  <div style={{ display: "flex", gap: 8, marginTop: 4, alignItems: "center" }}>
                    <span style={{ fontSize: "0.68rem", color: T.cyan }}>
                      {new Date(ev.date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                    </span>
                    {ev.artists?.length > 0 && (
                      <span style={{
                        fontSize: "0.65rem", color: T.muted,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>
                        {ev.artists.slice(0, 3).join(", ")}{ev.artists.length > 3 ? ` +${ev.artists.length - 3}` : ""}
                      </span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                    <span style={{ fontSize: "0.62rem", color: T.muted }}>
                      {ev.venue?.lat?.toFixed(3)}, {ev.venue?.lng?.toFixed(3)}
                    </span>
                    {ev.link && (
                      <a
                        href={ev.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: "0.62rem", color: T.cyan, textDecoration: "none" }}
                      >
                        Edmtrain →
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {displayEvents.length > 50 && (
            <p style={{ color: T.muted, fontSize: "0.75rem", textAlign: "center", padding: 8 }}>
              Showing 50 of {displayEvents.length} events
            </p>
          )}
        </div>
      )}

      {/* Attribution */}
      <div style={{ textAlign: "center", marginTop: 16 }}>
        <a
          href="https://edmtrain.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: T.muted, fontSize: "0.62rem", textDecoration: "none" }}
        >
          Event data powered by Edmtrain
        </a>
      </div>
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

const TABS = [
  { key: "flow",   label: "Quick Flow", Icon: Zap        },
  { key: "create", label: "Create",     Icon: Plus       },
  { key: "scan",   label: "Scan/Claim", Icon: RefreshCw  },
  { key: "browse", label: "Browse",     Icon: LayoutGrid },
  { key: "users",  label: "Users",      Icon: Users      },
  { key: "events", label: "Events",     Icon: Music      },
];

export default function AdminPanel({ onClose }) {
  const [tab, setTab] = useState("flow");
  const [adminKey, setAdminKey] = useState(localStorage.getItem(ADMIN_KEY_STORAGE) || "");

  const saveAdminKey = (val) => {
    setAdminKey(val);
    if (val) localStorage.setItem(ADMIN_KEY_STORAGE, val);
    else localStorage.removeItem(ADMIN_KEY_STORAGE);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 199,
          background: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(4px)",
        }}
      />

      {/* Drawer */}
      <div style={{
        position:  "fixed",
        top:       0,
        right:     0,
        bottom:    0,
        width:     "min(420px, 100vw)",
        zIndex:    200,
        background: T.drawerBg,
        borderLeft: "1px solid rgba(180,79,255,0.25)",
        boxShadow: "-16px 0 48px rgba(0,0,0,0.7)",
        display:   "flex",
        flexDirection: "column",
        overflowY: "hidden",
      }}>
        {/* Header */}
        <div style={{
          padding:      "16px 20px 14px",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          display:      "flex",
          alignItems:   "center",
          gap:          12,
          flexShrink:   0,
        }}>
          <div style={{
            width: 34, height: 34, borderRadius: 10, flexShrink: 0,
            background: "linear-gradient(135deg, rgba(180,79,255,0.3), rgba(255,60,172,0.3))",
            border: "1px solid rgba(180,79,255,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "1rem",
          }}>
            🔧
          </div>
          <div>
            <p style={{ margin: 0, fontWeight: 800, color: T.text, fontSize: "0.95rem" }}>
              Admin Panel
            </p>
            <p style={{ margin: 0, color: T.muted, fontSize: "0.72rem" }}>
              Backend testing tools
            </p>
          </div>
          <input
            type="password"
            value={adminKey}
            onChange={(e) => saveAdminKey(e.target.value)}
            placeholder="Admin key"
            title="Sent as x-admin-key on admin requests — set ADMIN_KEY on the server to require it"
            style={{
              marginLeft: "auto", width: 110, padding: "7px 10px",
              background: "rgba(255,255,255,0.05)",
              border: adminKey ? "1px solid rgba(57,255,20,0.35)" : "1px solid rgba(255,255,255,0.12)",
              borderRadius: 8, color: T.text, fontSize: "0.75rem", outline: "none",
            }}
          />
          <button
            onClick={onClose}
            style={{
              marginLeft: "auto", width: 32, height: 32, borderRadius: "50%",
              background: "rgba(255,255,255,0.05)", border: T.border,
              color: T.muted, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <X size={15} />
          </button>
        </div>

        {/* Tab bar */}
        <div style={{
          display:      "flex",
          padding:      "10px 12px 0",
          gap:          4,
          flexShrink:   0,
          overflowX:    "auto",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}>
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={{
                display:      "flex",
                alignItems:   "center",
                gap:          5,
                padding:      "7px 12px 10px",
                borderRadius: "10px 10px 0 0",
                fontWeight:   tab === key ? 700 : 500,
                fontSize:     "0.78rem",
                cursor:       "pointer",
                whiteSpace:   "nowrap",
                flexShrink:   0,
                background:   tab === key ? "rgba(180,79,255,0.12)" : "transparent",
                border:       "none",
                borderBottom: tab === key ? `2px solid ${T.purple}` : "2px solid transparent",
                color:        tab === key ? T.text : T.muted,
                transition:   "all 0.15s",
              }}
            >
              <Icon size={12} />
              {label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px 28px" }}>
          {tab === "flow"   && <QuickFlowTab />}
          {tab === "create" && <CreateTab />}
          {tab === "scan"   && <ScanClaimTab />}
          {tab === "browse" && <BrowseTab />}
          {tab === "users"  && <UsersTab />}
          {tab === "events" && <EventsTab />}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}
